import React, { useContext, useState, useRef, useEffect } from "react";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../BaseUrl";

//functions
import {
  _t,
  getCookie,
  getSystemSettings,
  customStyle,
} from "../../../../functions/Functions";
import { useTranslation } from "react-i18next";

//3rd party packages
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import Select from "react-select";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useReactToPrint } from "react-to-print";

//pages & includes
import ReportSidebar from "./ReportSidebar";

//context consumer
import { SettingsContext } from "../../../../contexts/Settings";
import { UserContext } from "../../../../contexts/User";
import { RestaurantContext } from "../../../../contexts/Restaurant";
import { FoodContext } from "../../../../contexts/Food";
import CustomSpinLoader from "../../../components/CustomSpinLoader";
import ReportTypeOptions from "../../../components/Reports/ReportTypeOptions";
import Moment from "react-moment";

const IngredientCostReport = () => {
  const { t } = useTranslation();
  
  //getting context values here
  let {
    loading,
    dataPaginating,
    setDataPaginating,
    generalSettings,
  } = useContext(SettingsContext);
  const { authUserInfo } = useContext(UserContext);
  const { branchForSearch } = useContext(RestaurantContext);
  const { foodForPOS, getFoodItemsForPOS } = useContext(FoodContext);

  //all data
  const [reportData, setReportData] = useState(null);
  const componentRef = useRef();
  const componentRefThermal = useRef();
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [branches, setBranches] = useState([]);
  const [selectedFoodItems, setSelectedFoodItems] = useState([]);
  const [generatedReport, setGeneratedReport] = useState(false);
  const [option, setOption] = useState("excel");

  useEffect(() => {
    if (!foodForPOS) {
      getFoodItemsForPOS();
    }
  }, []);

  let common_toast = {
    position: "bottom-center",
    closeButton: false,
    autoClose: 10000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    className: "text-center toast-notification",
  };

  const handleOption = (e) => {
    setOption(e.target.value);
  };

  const thermalPrint = useReactToPrint({
    content: () => componentRefThermal.current,
    pageStyle: `
        @page {
          margin: 15px 7px;
        }
      `,
  });

  //get excel reports filter
  const getExcelReport = () => {
    setReportData(null);
    setGeneratedReport(false);
    if (startDate !== null && endDate !== null) {
      getReport();
    } else {
      toast.error(
        `${_t(t("Please select from date and to date"))}`,
        common_toast
      );
    }
  };

  //get ingredient cost report
  const getReport = async () => {
    if (
      (authUserInfo.details.user_type === "staff" || authUserInfo.details.user_type === "manager") &&
      (!branches || branches.length === 0)
    ) {
      toast.error(`${_t(t("Please select branch to generate report"))}`, common_toast);
      return;
    }

    let fromDate = moment(startDate).format("YYYY-MM-DD");
    let toDate = moment(endDate).format("YYYY-MM-DD");
    const url = BASE_URL + "/settings/get-food-item-recipe-costing";
    setDataPaginating(true);
    
    const payload = {
        fromDate: fromDate,
        toDate: toDate,
        branch_id: branches ? branches.map(b => b.id) : [],
        food_item_ids: selectedFoodItems ? selectedFoodItems.map(f => f.id) : []
    };

    return axios
      .post(url, payload, {
        headers: { 
            Authorization: `Bearer ${getCookie()}`,
            "Content-Type": "application/json"
        },
      })
      .then((res) => {
        setGeneratedReport(true);
        setReportData(res.data);
        setDataPaginating(false);
        if (option === "excel") {
          componentRef.current.handleDownload();
        } else {
          thermalPrint();
        }
      })
      .catch((error) => {
        setDataPaginating(false);
        if(error.response.data.status === 0){
          toast.error(`${_t(t(error.response.data.message))}`, common_toast);
          return
        }
        toast.error(`${_t(t("Please try again"))}`, common_toast);
      });
  };

  const header = [
    { name: "Branch", key: "branch" },
    { name: "Date", key: "date" },
    { name: "Group", key: "group" },
    { name: "Ingredient ID", key: "ingredient_id" },
    { name: "Ingredient Name", key: "ingredient_name" },
    { name: "Quantity", key: "qty" },
    { name: "Avg Rate", key: "avg_rate" },
    { name: "Cost", key: "cost" },
  ];

 const tableHeading = () => {
    return (
      <>
        <h3 className="text-center xslg-text my-2">
          {getSystemSettings(generalSettings, "siteName")}
        </h3>
        <h3 className="text-center xslg-text my-2">
          {"Ingredient Cost Report"}
        </h3>
        {
          branches?.length > 0 && (
            <p className="text-center my-2">
              {branches.map(b => b.name).join(", ")}
            </p>
          )
        }
        <p className="text-center my-2">
          {
            startDate && endDate ? (
              <>
                FROM&nbsp;
                <Moment format="LL">
                  {startDate}
                </Moment>
                &nbsp;TO&nbsp;
                <Moment format="LL">{endDate}</Moment>
              </>
            ) : (
              <>
                Date&nbsp;
                <Moment format="LL">
                  {startDate}
                </Moment>
              </>
            )
          }
        </p>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{_t(t("Ingredient Cost Report"))}</title>
      </Helmet>
      <ReactHTMLTableToExcel
        id="test-table-xls-button"
        className="download-table-xls-button btn btn-success float-right mr-3 invisible"
        table="table-to-xls"
        filename={"Ingredient Cost Report"}
        sheet="sheet 1"
        buttonText="Ingredient Detail"
        ref={componentRef}
      />

      {/* main body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ReportSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div
                  className="fk-scroll--pos-menu table-bottom-info-hide"
                  data-simplebar
                >
                  <div className="t-pl-15 t-pr-15">
                    {/* next page data spin loading */}
                    <CustomSpinLoader loading={loading || dataPaginating} />

                    {/* spin loading ends */}

                    <div key="smtp-form">
                      <div className="gx-2 align-items-center t-pt-15 t-pb-15">
                        <div className="row t-mb-15 mb-md-0">
                          <ul className="t-list fk-breadcrumb col-6">
                            <li className="fk-breadcrumb__list">
                              <span className="pl-3 t-link fk-breadcrumb__link text-capitalize">
                                {_t(t("Ingredient Cost Report"))}
                              </span>
                            </li>
                          </ul>
                          <div className="col-6 d-flex justify-content-end">
                            <ReportTypeOptions
                              selectedOption={option}
                              onChange={handleOption}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="row mt-2">
                        <div className="col-2">
                          <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            peekNextMonth
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            className="form-control new-sm-text"
                            placeholderText={_t(t("From date"))}
                            shouldCloseOnSelect={false}
                          />
                        </div>

                        <div className="col-2">
                          <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            peekNextMonth
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            className="form-control new-sm-text"
                            placeholderText={_t(t("To date"))}
                            shouldCloseOnSelect={false}
                          />
                        </div>

                        <div className="col-3">
                          <Select
                            options={branchForSearch || []}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            value={branches}
                            styles={customStyle}
                            classNamePrefix="select"
                            isClearable
                            isMulti
                            className="table-text"
                            onChange={(vals)=>setBranches(vals)}
                            placeholder={_t(t("Select Branches")) + ".."}
                          />
                        </div>
                        
                        <div className="col-3">
                          <Select
                            options={foodForPOS || []}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            value={selectedFoodItems}
                            styles={customStyle}
                            classNamePrefix="select"
                            isClearable
                            isMulti
                            className="table-text"
                            onChange={(vals)=>setSelectedFoodItems(vals)}
                            placeholder={_t(t("Select Food Items")) + ".."}
                          />
                        </div>

                        <div className="col-2 text-right">
                          <button
                            className="ml-1 btn btn-block btn-primary sm-text py-2 rounded"
                            onClick={(e) => getExcelReport()}
                          >
                            GENERATE REPORT
                          </button>
                        </div>
                      </div>
                      <div className="row mt-3">
                        <div className="col-5"></div>
                      </div>
                      <div className="d-none">
                        <div ref={componentRefThermal}>
                          {reportData && generatedReport ? (
                            <div className="fk-print container-fluid mt-3">
                              <div>
                                <>
                                  <table id="table-to-xls" className="table table-bordered table-hover mt-2">
                                    {option === "excel" && (
                                      <>
                                        <tr>
                                          <th colspan={header.length}>
                                            {tableHeading()}
                                          </th>
                                        </tr>
                                        <tr></tr>
                                      </>
                                    )}
                                    {
                                      (Object.keys(reportData)?.length || reportData?.length) ? (
                                        <>
                                          <thead className="align-middle">
                                            <tr>
                                              {
                                                header.map((col, i) => (
                                                  <th
                                                    key={i}
                                                    scope="col"
                                                    className="border-1 border"
                                                    style={{ fontSize: "0.77rem" }}
                                                  >
                                                    {col.name}
                                                  </th>
                                                ))
                                              }
                                            </tr>
                                          </thead>
                                          <tbody className="align-middle">
                                            {
                                              reportData && Object.keys(reportData).map(group=>{
                                                return(
                                                  <>
                                                    <tr>
                                                      <th style={{minWidth:"150px"}}>{group}</th>
                                                    </tr>
                                                    {
                                                      reportData[group] && Object.keys(reportData[group]).map((grp)=>{
                                                        return(
                                                          <>
                                                            <tr>
                                                              <th></th>
                                                              <th>{grp}</th>
                                                            </tr>
                                                            {reportData[group][grp] && Object.keys(reportData[group][grp]).map((item)=>{
                                                              let cost = 0;
                                                              let rate = 0;
                                                              return(
                                                                <>
                                                                  <tr>
                                                                    <th></th>
                                                                    <th></th>
                                                                    <th>{item}</th>
                                                                  </tr>
                                                                  {reportData[group][grp][item] && reportData[group][grp][item].map((ingr)=>{
                                                                    cost += Number(ingr.cost);
                                                                    rate += Number(ingr.avg_rate);
                                                                    return(
                                                                      <tr>
                                                                        <th></th>
                                                                        <th></th>
                                                                        <th></th>
                                                                        <td>{ingr.ingredient_id}</td>
                                                                        <td>{ingr.ingredient_name}</td>
                                                                        <td>{ingr.qty}</td>
                                                                        <td>{ingr.avg_rate}</td>
                                                                        <td>{ingr.cost}</td>
                                                                      </tr>
                                                                    )
                                                                  })}
                                                                  <tr>
                                                                    <th colSpan={4}></th>
                                                                    <th colSpan={2}>Total</th>
                                                                    <td><strong>{rate}</strong></td>
                                                                    <td><strong>{cost}</strong></td>
                                                                  </tr>
                                                                  <tr></tr>
                                                                </>
                                                              )
                                                            })}
                                                          </>
                                                        )
                                                      })
                                                    }
                                                  </>
                                                )
                                              })
                                            }
                                          </tbody>
                                        </>
                                      ) : ""
                                    }
                                  </table>
                                </>
                              </div>
                            </div>
                          ) : ""}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Rightbar contents end*/}
          </div>
        </div>
      </main>
    </>
  );
};

export default IngredientCostReport;
